import '../App.css';
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import Highlightable from 'highlightable';
import React from 'react';
import SelectionToolbar from './SelectionToolbar';
import { logHighlightEvent } from '../logging';
import { ProgressCircle, Tooltip, Flex } from '@adobe/react-spectrum';

function TooltipSuggestionDocument({ ...properties }) {
    const [mouseWidgetInfo, setMouseWidgetInfo] = useState({ x: 0, y: 0, show: false, value: 0 });
    const [mouseIsDown, setMouseIsDown] = useState(false);
    const [mouseWidgetInstructions, setMouseWidgetInstructions] = useState("Only highlight important text.");
    const [dummyWordCount, setDummyWordCount] = useState(0);
    const tooltipRef = useRef(null);
    const [tooltipOffset, setTooltipOffset] = useState({ x: 0, y: 0 });
    const potentialWordCount = useRef(0);
    const highlightEventLogRef = useRef({});
    const mouseMoveEventsLogRef = useRef([]);

    function checkOverlappingRanges(left, right) {
        // the two ranges overlap
        if (right.start <= left.end) {
            return true
        }
        return false;
    }

    function mergeOverlappingRanges(left, right) {
        var newStart = Math.min(left.start, right.start);
        var newEnd = Math.max(left.end, right.end);
        var highlight = true;
        var showToolbar = false;

        // check whether the content should be highlighted right away
        if (properties.selectedTool !== "highlighter") {
            highlight = false;
            showToolbar = true;
        }
        return { start: newStart, end: newEnd, text: properties.passage.substring(newStart, newEnd + 1), highlight: highlight, showToolbar: showToolbar };
    }

    function mergeAllHighlights(newHighlight) {
        var highlights = properties.highlights.concat(newHighlight).sort((a, b) => a.start - b.start);

        function addDataToHighlight(highlightToEdit) {
            var highlight = true;
            var showToolbar = false;

            // check whether the content should be highlighted right away
            if (properties.selectedTool !== "highlighter") {
                highlight = false;
                showToolbar = true;
            }
            highlightToEdit.highlight = highlight;
            highlightToEdit.showToolbar = showToolbar;
        }

        for (var i = 0; i < highlights.length - 1; i++) {
            var left = highlights[i];
            var right = highlights[i + 1];
            if (checkOverlappingRanges(left, right)) {
                var merged = mergeOverlappingRanges(left, right);
                highlights.splice(i, 2, merged);
                i--;
            }
        }

        for (var i = 0; i < highlights.length; i++) {
            if (highlights[i].highlight === undefined || highlights[i].showToolbar === undefined) {
                addDataToHighlight(highlights[i]);
            }
        }


        return highlights;
    }

    function getHighlightedText(highlights) {
        var text = "";
        for (var i = 0; i < highlights.length; i++) {
            text += highlights[i].text + " ";
        }
        return text;
    }

    function checkRangeValid(range) {
        var rangeContents = document.getSelection().getRangeAt(0).cloneContents();
        var allSpans = rangeContents.querySelectorAll('span');
        var selectionSpans = [];
        allSpans.forEach((span) => {
            if (span.getAttribute('data-position') !== null) {
                selectionSpans.push(span);
            }
        });

        var startSpan = selectionSpans[0];
        var endSpan = selectionSpans[selectionSpans.length - 1];
        if (startSpan === undefined || endSpan === undefined) {
            return range;
        }

        // first check for any NaN values
        if (isNaN(range.start) || isNaN(range.end)) {
            var startIndex = parseInt(startSpan.getAttribute('data-position'));
            var endIndex = parseInt(endSpan.getAttribute('data-position'));
            range.start = startIndex;
            range.end = endIndex;
            range.text = properties.passage.substring(startIndex, endIndex + 1);
        }
        return range;
    }


    function onTextHighlighted() {
        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1) };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);
        var validHighlight = true;

        if (newWordCount <= properties.maxWordCount || !properties.constrainHighlights) {
            // do not change the word count with the cursor tool, otherwise you double count
            if (properties.selectedTool === "highlighter") {
                properties.setWordCount(newWordCount);
            }
            properties.setHighlights(mergedHighlights);
            properties.setShowError(false);
            document.getSelection().removeAllRanges();
            validHighlight = true;
        }

        else if (newWordCount > properties.maxWordCount) {
            properties.setShowError(true);
            validHighlight = false;
        }

        let log = {
            timestamp: Date.now(),
            type: "add",
            workerId: properties.workerId,
            participantId: properties.participantId,
            experiment: properties.experiment,
            condition: properties.condition,

            unsnappedRange: unsnappedRange,
            snappedRange: snappedRange,
            newWordCount: newWordCount,
        }
        // console.log(log);
        logHighlightEvent(log);
    }

    // helper function to snap the selection to words - https://stackoverflow.com/questions/7380190/select-whole-word-with-getselection
    function snapSelectionToWord() {
        var sel;

        // Check for existence of window.getSelection() and that it has a
        // modify() method. IE 9 has both selection APIs but no modify() method.
        if (window.getSelection && (sel = window.getSelection()).modify) {
            sel = window.getSelection();
            if (!sel.isCollapsed) {
                // Detect if selection is backwards
                var range = document.createRange();
                range.setStart(sel.anchorNode, sel.anchorOffset);
                range.setEnd(sel.focusNode, sel.focusOffset);
                var backwards = range.collapsed;
                range.detach();

                // modify() works on the focus of the selection
                var endNode = sel.focusNode,
                    endOffset = sel.focusOffset;
                sel.collapse(sel.anchorNode, sel.anchorOffset);

                var direction = [];
                if (backwards) {
                    direction = ['backward', 'forward'];
                } else {
                    direction = ['forward', 'backward'];
                }

                sel.modify("move", direction[0], "character");
                sel.modify("move", direction[1], "word");
                sel.extend(endNode, endOffset);
                sel.modify("extend", direction[1], "character");
                sel.modify("extend", direction[0], "word");

            }
        } else if ((sel === document.selection) && sel.type !== "Control") {
            var textRange = sel.createRange();
            if (textRange.text) {
                textRange.expand("word");
                // Move the end back to not include the word's trailing space(s),
                // if necessary
                while (/\s$/.test(textRange.text)) {
                    textRange.moveEnd("character", -1);
                }
                textRange.select();
            }
        }

        return sel;
    }

    function filterSelections(element) {
        return element.highlight;
    }
    // helper function to count the number of potential words after a selection, without double counting existing highlights
    function countNumPotentialWords() {
        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1), blurAmount: 0 };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);

        return newWordCount;
    }

    // filter out the selections that are not highlighted
    function mouseDown(e) {
        if (e.target.className !== "blackButton") {
            // hide temporary highlights
            var filteredHighlights = properties.highlights.filter(filterSelections);

            // hide all black buttons
            for (var i = 0; i < filteredHighlights.length; i++) {
                filteredHighlights[i].showToolbar = false;
            }

            properties.setHighlights(filteredHighlights);
            properties.setShowError(false);
        }

        // disable double clicking to highlight words and paragraphs
        if (e.detail > 1) {
            e.preventDefault();
            return; // prevent double click from triggering mouse down
        }

        document.getSelection().removeAllRanges();
        mouseMoveEventsLogRef.current = [];

        highlightEventLogRef.current = {
            type: "highlight",
            workerId: properties.workerId,
            participantId: properties.participantId,
            condition: properties.condition,
            experiment: properties.experiment,

            mouseDownTime: Date.now(),
            mouseDownX: e.clientX,
            mouseDownY: e.clientY,
            mouseDownWordCount: properties.wordCount,
        }

        setMouseIsDown(true);
    }

    function mouseUp(e) {
        if (e.detail > 1) {
            e.preventDefault();
            return; // prevent double click from triggering mouse up
        }
        setMouseIsDown(false);
        setMouseWidgetInfo({ x: 0, y: 0, show: false, value: 0 });

        // don't log normal clicks without any movement
        if (mouseMoveEventsLogRef.current.length > 0) {

            try {
                let sel = document.getSelection();
                let startNode = sel.anchorNode.parentNode;
                let endNode = sel.focusNode.parentNode;
                let unsnappedStartIndex = parseInt(startNode.getAttribute('data-position'));
                let unsnappedEndIndex = parseInt(endNode.getAttribute('data-position'));
                let unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
                let unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

                highlightEventLogRef.current = {
                    ...highlightEventLogRef.current,
                    mouseUpTime: Date.now(),
                    mouseUpX: e.clientX,
                    mouseUpY: e.clientY,
                    mouseUpWordCount: potentialWordCount.current,
                    mouseUpUnsnappedRange: unsnappedRange,
                    mouseUpStartNodeBounds: startNode.getBoundingClientRect().toJSON(),
                    mouseUpEndNodeBounds: endNode.getBoundingClientRect().toJSON(),

                    mouseMoveEvents: mouseMoveEventsLogRef.current,
                }
                // console.log(highlightEventLogRef.current);
                logHighlightEvent(highlightEventLogRef.current);
            } catch (error) {
                console.error("Error logging highlight event:", error);
            }
        }
    }

    function mouseMove(e) {
        let textSelection = document.getSelection().toString();
        setMouseWidgetInfo({ x: e.clientX, y: e.clientY });

        // determine if they need to wait at all
        if (textSelection.length > 0 && textSelection !== "" && mouseIsDown) {
            let newWordCount = countNumPotentialWords();
            potentialWordCount.current = newWordCount;
            setDummyWordCount(newWordCount);

            // for the highlighting experiment, keep the technique on at all times
            if (properties.experiment === "exp2-highlights") {
                setMouseWidgetInfo({ x: e.clientX, y: e.clientY, show: true });
            }

            // for the other experiments, only trigger when the user is selecting too many words (over 150)
            else {
                // they will be selecting too many words, show the tooltip
                if (newWordCount > properties.targetWordCount) {
                    setMouseWidgetInfo({ x: e.clientX, y: e.clientY, show: true });
                }

                // they are selecting the right amount of words, so they don't need to wait, don't show the mouse widget
                else {
                    setMouseWidgetInfo({ x: e.clientX, y: e.clientY, show: false });
                }
            }

            if (mouseMoveEventsLogRef.current.length === 0 || mouseMoveEventsLogRef.current[mouseMoveEventsLogRef.current.length - 1].wordCount !== newWordCount) {
                // add data to the movement log
                let sel = document.getSelection();
                let startNode = sel.anchorNode.parentNode;
                let endNode = sel.focusNode.parentNode;
                let unsnappedStartIndex = parseInt(startNode.getAttribute('data-position'));
                let unsnappedEndIndex = parseInt(endNode.getAttribute('data-position'));
                let unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
                let unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

                mouseMoveEventsLogRef.current.push({
                    timestamp: Date.now(),
                    x: e.clientX,
                    y: e.clientY,
                    wordCount: newWordCount,
                    unsnappedRange: unsnappedRange,
                    startNodeBounds: startNode.getBoundingClientRect().toJSON(),
                    endNodeBounds: endNode.getBoundingClientRect().toJSON(),
                });
            }

        }
    }

    useEffect(() => {
        if (tooltipRef.current) {
            let tooltip = tooltipRef.current.UNSAFE_getDOMNode();
            let bounds = tooltip.getBoundingClientRect();
            setTooltipOffset({ x: bounds.width / 2, y: bounds.height + 20 });
        }
    }, [mouseWidgetInstructions]);

    // listen to scrolling to hide the error message
    useEffect(() => {
        const onScroll = (event) => console.info("scrolling", event);

        window.addEventListener('scroll', mouseScroll);

        return () => {
            window.removeEventListener('scroll', mouseScroll);
        }
    }, []);

    function mouseScroll(e) {
        properties.setShowError(false);
    }

    return (
        <>
            <div style={{ backgroundColor: "#f8f8f8" }} onMouseDown={e => mouseDown(e)} onCopy={(e) => { e.preventDefault() }} onMouseUp={e => mouseUp(e)} onMouseMove={e => mouseMove(e)}>
                <Tooltip UNSAFE_className="no-select" ref={tooltipRef} isOpen={mouseWidgetInfo.show} left={mouseWidgetInfo.x - tooltipOffset.x} top={mouseWidgetInfo.y - tooltipOffset.y} placement='top' position={"fixed"} zIndex={10000} UNSAFE_style={{ textAlign: "center", display: "unset" }}>{mouseWidgetInstructions}</Tooltip>
                <Flex direction="row" justifyContent={'center'}>
                    <div id="textToRead" className='documentReader'>
                        <Highlightable
                            ranges={properties.highlights}
                            enabled={properties.maxWordCount === 0 ? false : true} // disable highlighting if the max word count is 0
                            highlightStyle={range => {
                                if (range.highlight) {
                                    return {
                                        backgroundColor: '#FDEAA3',
                                    }
                                }
                                else {
                                    return {
                                        backgroundColor: '#b3d8ff',
                                    }
                                }
                            }}
                            onTextHighlighted={onTextHighlighted}
                            text={properties.passage}
                            nodeRenderer={(index, range, text) => {
                                var currChar = text.charAt(index);
                                if (currChar === '\n') {
                                    return (<><span data-position={index} range={range}><br /><br /></span></>)
                                }
                                return (
                                    <span data-position={index} range={range}>{currChar}</span>
                                )
                            }}
                            rangeRenderer={range => {
                                // helper function to update the tool bar visibility
                                function updateShowToolbar(newValue) {
                                    var index = properties.highlights.indexOf(range[0].props.range);
                                    var newHighlight = properties.highlights[index];
                                    newHighlight.showToolbar = newValue;
                                    properties.setHighlights(properties.highlights.toSpliced(index, 1).concat(newHighlight));
                                }

                                function mouseEnter(e) {
                                    e.style.outline = "2px solid #337AC2";
                                    e.style.cursor = "pointer";
                                }

                                function mouseLeave(e) {
                                    if (range[0].props.range.highlight)
                                        e.style.outline = "2px solid #FDEAA3";
                                    else
                                        e.style.outline = "2px solid #b3d8ff";
                                    e.style.cursor = "auto";
                                }

                                function mouseClick(e) {
                                    updateShowToolbar(true);
                                }

                                // creating an actual highlight
                                if (range[0].props.range.highlight) {
                                    return (
                                        <div style={{ display: 'inline', position: 'relative' }}>
                                            <SelectionToolbar
                                                workerId={properties.workerId}
                                                participantId={properties.participantId}
                                                experiment={properties.experiment}
                                                condition={properties.condition}

                                                passage={properties.passage}
                                                countNumWords={properties.countNumWords}

                                                wordCount={properties.wordCount}
                                                setWordCount={properties.setWordCount}
                                                setProgressWordCount={properties.setProgressWordCount}

                                                selectedTool={'highlighter'}
                                                updateShowToolbar={updateShowToolbar}

                                                range={range}
                                                highlights={properties.highlights}
                                                setHighlights={properties.setHighlights} />
                                            <span style={{ outline: "2px solid #FDEAA3", backgroundColor: "#FDEAA3" }} onMouseEnter={e => mouseEnter(e.target.parentNode)} onMouseLeave={e => mouseLeave(e.target.parentNode)} onClick={e => mouseClick(e.target.parentNode)}>{range}</span>
                                        </div>
                                    );

                                }

                                // creating a fake selection effect
                                else {
                                    return (
                                        <div style={{ display: 'inline', position: 'relative' }}>
                                            <SelectionToolbar
                                                workerId={properties.workerId}
                                                participantId={properties.participantId}
                                                experiment={properties.experiment}
                                                condition={properties.condition}

                                                passage={properties.passage}
                                                countNumWords={properties.countNumWords}

                                                wordCount={properties.wordCount}
                                                setWordCount={properties.setWordCount}
                                                setProgressWordCount={properties.setProgressWordCount}
                                                setShadowWordCount={properties.setShadowWordCount}

                                                selectedTool={'cursor'}
                                                updateShowToolbar={updateShowToolbar}

                                                range={range}

                                                highlights={properties.highlights}
                                                setHighlights={properties.setHighlights} />
                                            <span style={{ outline: "2px solid #b3d8ff", backgroundColor: "#b3d8ff" }}>{range}</span>
                                        </div>)
                                }
                            }}
                        />

                    </div>
                </Flex>
            </div>
        </>
    );
}

export default TooltipSuggestionDocument;